/**
 * The two decisions behind the exit-questionnaire block of the adoption screen.
 *
 * Both are about meaning rather than markup — which questions are drawn and which are read, and
 * which questionnaire a link points at when none of them is being asked — so they live here, where
 * they can be tested without rendering anything.
 *
 * @module Pages/BUs/OptInFeatures
 */

import type { Survey, SurveyQuestionResult } from '@/Pages/BUs/Surveys/Types/Surveys.types';

export interface ExitQuestionSplit {
  /** Choices, scales and grids: the distribution the old "Motivos de saída" panel used to draw. */
  charted: SurveyQuestionResult[];
  /**
   * What the free-text filter offers, which is **not** the same set as "questions of type text".
   *
   * A choice question carries text too, in the box behind "Outro: ___" — and on an exit
   * questionnaire that box is where the option list turns out to have been missing the actual
   * reason somebody left.
   */
  textFilterOptions: { key: string; label: string }[];
}

/**
 * Text questions are listed, never charted.
 *
 * *The reason changed, the behaviour did not.* It used to be a unit problem: the results screen's
 * text card reads "N pessoas escreveram alguma coisa", which was false here while the exit
 * questionnaire was asked at every exit and `answered` counted responses. Since R33 it is one
 * answer per person per version everywhere, so the card would now be true — and it is still left
 * out, because this block lists the free text right below, under "O que escreveram". The card is a
 * headline count plus a link to that same list: above the list itself, it is a duplicate.
 */
export const splitExitQuestions = (
  questions: SurveyQuestionResult[] | undefined,
): ExitQuestionSplit => {
  const all = questions ?? [];

  return {
    charted: all.filter(
      question => question.type !== 'SHORT_TEXT' && question.type !== 'LONG_TEXT',
    ),
    textFilterOptions: all
      .filter(
        question =>
          question.type === 'SHORT_TEXT' ||
          question.type === 'LONG_TEXT' ||
          question.type === 'SINGLE_CHOICE' ||
          question.type === 'MULTI_CHOICE',
      )
      .map(question => ({ key: question.key, label: question.label })),
  };
};

/**
 * Which questionnaire the block is about.
 *
 * The served one whenever there is one — the same rule the BFF serves by, resolved by
 * `pickServedExitSurvey`. With none servable the block still has to name something, or the R35
 * warning would be about a questionnaire the screen refuses to identify; the **newest** is the
 * choice, because it is the one the ④ block of the feature form shows first and the one somebody
 * is most likely to be in the middle of writing.
 *
 * `null` only when there is no questionnaire at all — and then the block does not exist.
 */
export const exitSurveyInFocus = (served: Survey | null, surveys: Survey[]): Survey | null => {
  if (served) return served;
  if (surveys.length === 0) return null;

  return [...surveys].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];
};
